import { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Chip, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { formatLocalCurrency } from "../../utils/currency";
import { HS_AGENT, hsRequest } from "./api";

interface ClientData { name: string; cpf: string; motherName: string; birthDate: string; pix: string; pixType: string | null; rg: string }

const PIX_TYPES = [
    { v: 'cpf', l: 'CPF' }, { v: 'phone', l: 'Telefone' }, { v: 'email', l: 'E-mail' }, { v: 'random', l: 'Chave aleatória' },
];

/**
 * Trabalho do atendente no lead: dados do cliente (CPF e cadastro; contato fica oculto), consulta,
 * simulação e geração de proposta. A mensagem ao cliente continua só pelo chat do atendente.
 */
export function LeadWork({ lead }: { lead: any }) {
    const base = `${HS_AGENT}/leads/${lead.id}`;
    const [client, setClient] = useState<ClientData | null>(null);
    const [error, setError] = useState('');
    const [ok, setOk] = useState('');
    const [busy, setBusy] = useState('');

    const loadClient = useCallback(async () => {
        try { setClient(await hsRequest<ClientData>('GET', `${base}/client`)); } catch (e: any) { setError(e.message); }
    }, [base]);
    useEffect(() => { loadClient(); }, [loadClient]);

    async function run<T>(key: string, fn: () => Promise<T>): Promise<T | undefined> {
        setBusy(key); setError(''); setOk('');
        try { return await fn(); } catch (e: any) { setError(e.message); return undefined; } finally { setBusy(''); }
    }

    async function saveClient() {
        if (!client) return;
        const r = await run('save', () => hsRequest<ClientData>('PUT', `${base}/client`, {
            name: client.name, motherName: client.motherName, birthDate: client.birthDate, rg: client.rg, pix: client.pix, pixType: client.pixType,
        }));
        if (r) { setClient(r); setOk('Dados salvos.'); }
    }
    const set = (k: keyof ClientData, v: string) => client && setClient({ ...client, [k]: v });
    const missing = client ? !client.name || !client.motherName || !client.birthDate || !client.pix || !client.pixType : true;

    return (
        <Box className="sales-client-work" sx={{ p: { xs: 2, sm: 3 } }}>
            {error ? <Alert severity="error" onClose={() => setError('')} sx={{ mb: 1 }}>{error}</Alert> : null}
            {ok ? <Alert severity="success" onClose={() => setOk('')} sx={{ mb: 1 }}>{ok}</Alert> : null}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Dados do cliente</Typography>
            {client ? (
                <Box className="sales-client-fields" sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                    <TextField size="small" label="CPF" value={client.cpf} disabled sx={{ width: 150 }} />
                    <TextField size="small" label="Nome completo" value={client.name} onChange={(e) => set('name', e.target.value)} sx={{ minWidth: 240 }} />
                    <TextField size="small" label="Nome da mãe" value={client.motherName} onChange={(e) => set('motherName', e.target.value)} sx={{ minWidth: 240 }} />
                    <TextField size="small" label="Nascimento (dd/mm/aaaa)" value={client.birthDate} onChange={(e) => set('birthDate', e.target.value)} sx={{ width: 180 }} />
                    <TextField size="small" label="RG" value={client.rg} onChange={(e) => set('rg', e.target.value)} sx={{ width: 140 }} />
                    <TextField size="small" select label="Tipo do PIX" value={client.pixType || ''} onChange={(e) => set('pixType', e.target.value)} sx={{ width: 160 }}>
                        {PIX_TYPES.map((p) => <MenuItem key={p.v} value={p.v}>{p.l}</MenuItem>)}
                    </TextField>
                    <TextField size="small" label="Chave PIX" value={client.pix} onChange={(e) => set('pix', e.target.value)} sx={{ minWidth: 240 }} />
                    <LoadingButton variant="outlined" loading={busy === 'save'} onClick={saveClient}>Salvar dados</LoadingButton>
                </Box>
            ) : <Typography variant="body2">Carregando…</Typography>}
            {lead.product === 'fgts'
                ? <FgtsWork base={base} run={run} busy={busy} missing={missing} onDone={setOk} />
                : <CltWork base={base} run={run} busy={busy} missing={missing} onDone={setOk} />}
        </Box>
    );
}

type RunFn = <T>(key: string, fn: () => Promise<T>) => Promise<T | undefined>;
interface WorkProps { base: string; run: RunFn; busy: string; missing: boolean; onDone: (m: string) => void }

function FgtsWork({ base, run, busy, missing, onDone }: WorkProps) {
    const [consult, setConsult] = useState<any>(null);
    const [sim, setSim] = useState<any>(null);
    const [proposal, setProposal] = useState<any>(null);

    async function doConsult() {
        setSim(null); setProposal(null);
        const r = await run('consult', () => hsRequest<any>('POST', `${base}/fgts/consult`));
        if (r) setConsult(r);
    }
    async function doSimulate() {
        const r = await run('sim', () => hsRequest<any>('POST', `${base}/fgts/simulate`, { consultId: consult.consultId }));
        if (r) setSim(r);
    }
    async function doProposal() {
        if (!window.confirm('Gerar a proposta de FGTS com estes dados?')) return;
        const r = await run('proposal', () => hsRequest<any>('POST', `${base}/fgts/proposal`, { consultId: consult.consultId, simulationId: sim.simulationId }));
        if (r) { setProposal(r); onDone('Proposta gerada.'); }
    }
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>FGTS</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                <LoadingButton size="small" variant="outlined" loading={busy === 'consult'} onClick={doConsult}>Consultar saldo</LoadingButton>
                <LoadingButton size="small" variant="outlined" disabled={!consult?.ok} loading={busy === 'sim'} onClick={doSimulate}>Simular</LoadingButton>
                <LoadingButton size="small" variant="contained" disabled={!sim || missing || !!proposal} loading={busy === 'proposal'} onClick={doProposal}>Gerar proposta</LoadingButton>
                {missing ? <Typography variant="caption" color="text.secondary">Preencha e salve todos os dados para gerar a proposta.</Typography> : null}
            </Box>
            {consult ? (
                consult.ok
                    ? <Chip color="success" label={`Saldo disponível: ${formatLocalCurrency(consult.availableAmount)}${consult.cached ? ' (consulta recente)' : ''}`} sx={{ mr: 1 }} />
                    : <Alert severity="warning" sx={{ mb: 1 }}>{consult.message || `Sem saldo (${consult.result}).`}</Alert>
            ) : null}
            {sim ? <Chip label={`Valor liberado: ${formatLocalCurrency(sim.availableBalance)} • tabela ${String(sim.tableFeeId).slice(0, 8)}…`} sx={{ mr: 1 }} /> : null}
            {proposal ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                    Proposta {proposal.proposalId} criada ({proposal.status}). Link de formalização: <a href={proposal.link} target="_blank" rel="noreferrer">{proposal.link}</a> — envie ao cliente pelo chat.
                </Alert>
            ) : null}
        </Box>
    );
}

function CltWork({ base, run, busy, missing, onDone }: WorkProps) {
    const [status, setStatus] = useState<any>(null);
    const [options, setOptions] = useState<any[] | null>(null);
    const [proposal, setProposal] = useState<any>(null);

    async function doConsult() {
        setOptions(null); setProposal(null);
        const r = await run('consult', () => hsRequest<any>('POST', `${base}/clt/consult`));
        if (r) setStatus({ status: r.status === 'noConsultable' || r.status === 'blocked' ? r.status : null, reason: r.reason, canSimulate: r.margin?.status === 'SUCCESS', margin: r.margin, existing: r.status });
    }
    async function refresh() {
        const r = await run('status', () => hsRequest<any>('GET', `${base}/clt/status`));
        if (r) setStatus(r);
    }
    async function doSimulate() {
        const r = await run('sim', () => hsRequest<any>('POST', `${base}/clt/simulate`));
        if (r) setOptions(r.options);
    }
    async function pick(o: any) {
        if (!window.confirm(`Gerar proposta CLT de ${o.installments}x de ${formatLocalCurrency(o.installmentValue)}?`)) return;
        const r = await run('proposal', () => hsRequest<any>('POST', `${base}/clt/proposal`, { installments: o.installments, installmentValue: o.installmentValue, insured: o.insured }));
        if (r) { setProposal(r); onDone('Proposta gerada.'); }
    }
    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>CLT</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                <LoadingButton size="small" variant="outlined" loading={busy === 'consult'} onClick={doConsult}>Consultar margem</LoadingButton>
                <LoadingButton size="small" variant="outlined" loading={busy === 'status'} onClick={refresh}>Atualizar resultado</LoadingButton>
                <LoadingButton size="small" variant="outlined" disabled={!status?.canSimulate} loading={busy === 'sim'} onClick={doSimulate}>Simular</LoadingButton>
            </Box>
            {status ? (
                status.canSimulate ? <Chip color="success" label="Margem liberada" sx={{ mb: 1 }} />
                    : <Alert severity="info" sx={{ mb: 1 }}>{status.reason || (status.status ? `Consulta: ${status.status}${status.rejectReason ? ` (${status.rejectReason})` : ''}` : 'Consulta em processamento — clique em "Atualizar resultado" em instantes.')}</Alert>
            ) : null}
            {options ? (
                options.length === 0 ? <Alert severity="warning">O banco recusou todas as combinações de prazo e parcela.</Alert> : (
                    <>
                    <Box sx={{ display: { xs: 'grid', sm: 'none' }, gap: 1.5 }}>
                        {options.map((o, i) => <Paper variant="outlined" key={i} sx={{ p: 2, borderRadius: 2 }}>
                            <Typography variant="caption" color="text.secondary">Valor líquido</Typography>
                            <Typography variant="h6" fontWeight={700}>{formatLocalCurrency(o.liquidValue)}</Typography>
                            <Typography variant="body2" sx={{ my: 1 }}>{o.installments}x de {formatLocalCurrency(o.installmentValue)} • {o.insured ? 'Com seguro' : 'Sem seguro'}</Typography>
                            <Button fullWidth variant="contained" disabled={missing || !!proposal || !!busy} onClick={() => pick(o)}>Gerar proposta</Button>
                        </Paper>)}
                    </Box>
                    <Box sx={{ overflowX: 'auto', display: { xs: 'none', sm: 'block' } }}><Table size="small">
                        <TableHead><TableRow><TableCell>Prazo</TableCell><TableCell>Parcela</TableCell><TableCell>Líquido</TableCell><TableCell>Seguro</TableCell><TableCell /></TableRow></TableHead>
                        <TableBody>
                            {options.map((o, i) => (
                                <TableRow key={i}>
                                    <TableCell>{o.installments}x</TableCell><TableCell>{formatLocalCurrency(o.installmentValue)}</TableCell>
                                    <TableCell>{formatLocalCurrency(o.liquidValue)}</TableCell><TableCell>{o.insured ? 'Sim' : 'Não'}</TableCell>
                                    <TableCell align="right"><Button size="small" variant="contained" disabled={missing || !!proposal || busy === 'proposal'} onClick={() => pick(o)}>Gerar proposta</Button></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table></Box>
                    </>
                )
            ) : null}
            {options && missing ? <Typography variant="caption" color="text.secondary">Preencha e salve o PIX para gerar a proposta.</Typography> : null}
            {proposal ? (
                <Alert severity="success" sx={{ mt: 1 }}>
                    Proposta {proposal.proposalId} criada ({proposal.installments}x de {formatLocalCurrency(proposal.installmentValue)}). Link: <a href={proposal.link} target="_blank" rel="noreferrer">{proposal.link}</a> — envie ao cliente pelo chat.
                </Alert>
            ) : null}
        </Box>
    );
}
